import React, { useEffect, useState, useRef } from "react";
import {
  PenIcon, Trash2Icon, SaveIcon, PlusCircle, ChevronUp, ChevronDown,
  CheckCircle2, ListIcon, Quote, Pilcrow, Heading1, ImagePlus,
  X, Code2, AlertCircle, GripVertical, Bold, Table,
} from "lucide-react";

/* ─── Constants ───────────────────────────────────────────────── */
const BLOCK_TYPES = [
  { id: "h1",          label: "H1",        icon: Heading1,    group: "heading" },
  { id: "h2",          label: "H2",        icon: Heading1,    group: "heading" },
  { id: "h3",          label: "H3",        icon: Heading1,    group: "heading" },
  { id: "h4",          label: "H4",        icon: Heading1,    group: "heading" },
  { id: "h5",          label: "H5",        icon: Heading1,    group: "heading" },
  { id: "h6",          label: "H6",        icon: Heading1,    group: "heading" },
  { id: "paragraph",   label: "Paragraph", icon: Pilcrow,     group: "text"    },
  { id: "highlighted", label: "Highlight", icon: AlertCircle, group: "text"    },
  { id: "blockquote",  label: "Quote",     icon: Quote,       group: "text"    },
  { id: "code",        label: "Code",      icon: Code2,       group: "text"    },
  { id: "bold",        label: "Bold",      icon: Bold,        group: "text"    },
  { id: "list",        label: "List",      icon: ListIcon,    group: "list"    },
  { id: "table",       label: "Table",     icon: Table,       group: "table"   },
  { id: "image",       label: "Image",     icon: ImagePlus,   group: "media"   },
];

const GROUP_LABELS = {
  heading: "Headings",
  text: "Text",
  list: "List",
  table: "Table",
  media: "Media",
};

/* ─── Helpers ────────────────────────────────────────────────── */
const parseContentToArray = (htmlString) => {
  if (!htmlString || typeof htmlString !== "string") return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const readerContent = doc.querySelector(".reader-content");
    if (!readerContent) return [];
    return Array.from(readerContent.children).map((el) => el.outerHTML);
  } catch { return []; }
};

const buildHTML = (type, value, className) => {
  const cls = className.trim() ? ` class="${className.trim()}"` : "";
  switch (type) {
    case "h1": case "h2": case "h3": case "h4": case "h5": case "h6":
      return `<${type}${cls}>${value}</${type}>`;
    case "paragraph":   return `<p${cls}>${value}</p>`;
    case "highlighted": return `<p class="lead ${className.trim()}">${value}</p>`;
    case "blockquote":  return `<blockquote${cls}><p>${value}</p></blockquote>`;
    case "code":        return `<pre${cls}><code>${value}</code></pre>`;
    case "bold":        return `<p${cls}><strong>${value}</strong></p>`;
    default: return null;
  }
};

const blockLabel = (html) => {
  try {
    const el = new DOMParser().parseFromString(html, "text/html").body.firstElementChild;
    if (!el) return "Block";
    const tag = el.tagName.toLowerCase();
    if (/^h[1-6]$/.test(tag)) return tag.toUpperCase();
    if (tag === "p") {
      if (el.classList.contains("lead")) return "Highlight";
      if (el.querySelector("strong") && el.children.length === 1) return "Bold";
      return "Paragraph";
    }
    if (tag === "blockquote") return "Quote";
    if (tag === "pre") return "Code";
    if (tag === "ul") return "List";
    if (tag === "table") return "Table";
    if (tag === "figure" || tag === "img") return "Image";
    return tag;
  } catch { return "Block"; }
};

const LABEL_COLORS = {
  H1: "bg-violet-50 text-violet-600 border-violet-200",
  H2: "bg-violet-50 text-violet-600 border-violet-200",
  H3: "bg-violet-50 text-violet-600 border-violet-200",
  H4: "bg-violet-50 text-violet-600 border-violet-200",
  H5: "bg-violet-50 text-violet-600 border-violet-200",
  H6: "bg-violet-50 text-violet-600 border-violet-200",
  Paragraph:  "bg-[#0d1b2e] text-slate-500 border-[#1d3350]",
  Highlight:  "bg-amber-50 text-amber-600 border-amber-200",
  Quote:      "bg-sky-50 text-sky-600 border-sky-200",
  Code:       "bg-emerald-50 text-emerald-600 border-emerald-200",
  Bold:       "bg-indigo-50 text-indigo-600 border-indigo-200",
  List:       "bg-orange-50 text-orange-600 border-orange-200",
  Table:      "bg-teal-50 text-teal-600 border-teal-200",
  Image:      "bg-pink-50 text-pink-600 border-pink-200",
};

const inputCls =
  "w-full rounded-lg border border-[#1d3350] bg-[#0f1c2e] px-3 py-2.5 text-sm text-slate-200 " +
  "placeholder-slate-400 shadow-sm transition focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100";

/* ─── BlockTypeButton ─────────────────────────────────────────── */
const BlockTypeButton = ({ type, active, onClick }) => {
  const Icon = type.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${
        active
          ? "border-indigo-400 bg-indigo-50 text-indigo-600 shadow-sm"
          : "border-[#1d3350] bg-[#0f1c2e] text-slate-500 hover:border-slate-300 hover:bg-[#0d1b2e] shadow-sm"
      }`}
    >
      <Icon size={12} />
      {type.label}
    </button>
  );
};

/* ─── ContentItem ─────────────────────────────────────────────── */
const ContentItem = ({ html, index, total, onEdit, onRemove, onMove }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(html);
  const textareaRef           = useRef(null);
  const label                 = blockLabel(html);
  const colorCls              = LABEL_COLORS[label] || "bg-[#0d1b2e] text-slate-500 border-[#1d3350]";

  const save   = () => { onEdit(index, draft); setEditing(false); };
  const cancel = () => { setDraft(html); setEditing(false); };

  useEffect(() => {
    if (editing && textareaRef.current) textareaRef.current.focus();
  }, [editing]);

  return (
    <div className="group rounded-xl border border-[#1d3350] bg-[#0f1c2e] shadow-sm transition hover:border-slate-300 hover:shadow-md">
      {/* header */}
      <div className="flex items-center gap-2 border-b border-[#17283f] px-3 py-2">
        <GripVertical size={13} className="shrink-0 text-slate-300" />
        <span className={`rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${colorCls}`}>
          {label}
        </span>
        <div className="flex flex-1 items-center justify-end gap-1 opacity-0 transition group-hover:opacity-100">
          <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)}
            className="rounded p-1 text-slate-400 hover:bg-[#13233a] hover:text-slate-300 disabled:opacity-20 transition" title="Move up">
            <ChevronUp size={13} />
          </button>
          <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)}
            className="rounded p-1 text-slate-400 hover:bg-[#13233a] hover:text-slate-300 disabled:opacity-20 transition" title="Move down">
            <ChevronDown size={13} />
          </button>
          <button type="button" onClick={() => setEditing((v) => !v)}
            className={`rounded p-1 transition hover:bg-[#13233a] ${editing ? "text-indigo-500" : "text-slate-400 hover:text-slate-300"}`} title="Edit raw HTML">
            <PenIcon size={13} />
          </button>
          <button type="button" onClick={() => onRemove(index)}
            className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 transition" title="Remove">
            <Trash2Icon size={13} />
          </button>
        </div>
      </div>

      {/* content */}
      <div className="p-3">
        {editing ? (
          <div className="space-y-2">
            <textarea ref={textareaRef} value={draft} onChange={(e) => setDraft(e.target.value)}
              rows={4}
              className="w-full resize-y rounded-lg border border-indigo-200 bg-[#0d1b2e] p-2.5 font-mono text-xs text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-100" />
            <div className="flex gap-2">
              <button type="button" onClick={save}
                className="flex items-center gap-1 rounded-lg bg-indigo-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-600 transition">
                <CheckCircle2 size={12} /> Save
              </button>
              <button type="button" onClick={cancel}
                className="flex items-center gap-1 rounded-lg border border-[#1d3350] px-3 py-1.5 text-xs text-slate-500 hover:bg-[#0d1b2e] transition">
                <X size={12} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className="reader-content text-sm text-slate-300 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-slate-100 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-slate-100 [&_h3]:font-bold [&_h3]:text-slate-100 [&_strong]:font-bold [&_strong]:text-slate-100 [&_blockquote]:border-l-2 [&_blockquote]:border-indigo-400 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-slate-500 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-[#13233a] [&_pre]:p-3 [&_pre]:font-mono [&_pre]:text-xs [&_.lead]:rounded-lg [&_.lead]:bg-amber-50 [&_.lead]:px-3 [&_.lead]:py-2 [&_.lead]:text-amber-700 [&_ul]:list-disc [&_ul]:pl-5 [&_img]:max-w-[min(400px,40vw)] [&_img]:rounded-lg [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs [&_th]:border [&_th]:border-[#1d3350] [&_th]:bg-[#0d1b2e] [&_th]:px-3 [&_th]:py-2 [&_th]:font-semibold [&_th]:text-slate-300 [&_td]:border [&_td]:border-[#1d3350] [&_td]:px-3 [&_td]:py-2 [&_td]:text-slate-400"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </div>
    </div>
  );
};

/* ─── Image Block Input ───────────────────────────────────────── */
const ImageBlockInput = ({ onAdd }) => {
  const [src, setSrc]         = useState("");
  const [alt, setAlt]         = useState("");
  const [caption, setCaption] = useState("");

  const handleAdd = () => {
    if (!src.trim()) return;
    const figureHtml = `<figure style="margin:16px 0;"><img src="${src.trim()}" alt="${alt.trim()}" style="max-width:min(400px,40vw);width:100%;height:auto;border-radius:8px;" />${caption.trim() ? `<figcaption style="font-size:0.8em;color:#94a3b8;margin-top:6px;">${caption.trim()}</figcaption>` : ""}</figure>`;
    onAdd(figureHtml);
    setSrc(""); setAlt(""); setCaption("");
  };

  return (
    <div className="space-y-3 rounded-xl border border-indigo-100 bg-indigo-50/50 p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-indigo-500">
        <ImagePlus size={13} /> Image Block
      </div>
      <div className="space-y-2">
        <input type="url" value={src} onChange={(e) => setSrc(e.target.value)}
          placeholder="Image URL  https://…" className={inputCls} autoFocus />
        <div className="grid grid-cols-2 gap-2">
          <input type="text" value={alt} onChange={(e) => setAlt(e.target.value)}
            placeholder="Alt text" className={inputCls} />
          <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption (optional)" className={inputCls} />
        </div>
        {src && (
          <div className="overflow-hidden rounded-lg border border-[#1d3350] bg-[#0f1c2e] p-2">
            <img src={src} alt={alt || "preview"} className="max-h-40 rounded object-contain"
              style={{ maxWidth: "min(400px, 40vw)" }}
              onError={(e) => { e.target.style.display = "none"; }} />
          </div>
        )}
      </div>
      <button type="button" onClick={handleAdd} disabled={!src.trim()}
        className="flex items-center gap-1.5 rounded-lg bg-indigo-500 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-600 disabled:opacity-40 transition">
        <PlusCircle size={13} /> Add image block
      </button>
    </div>
  );
};

/* ─── List Builder ────────────────────────────────────────────── */
const ListBuilder = ({ onAdd }) => {
  const [items, setItems] = useState([""]);

  const updateItem = (i, val) => { const next = [...items]; next[i] = val; setItems(next); };
  const addRow     = () => setItems([...items, ""]);
  const removeRow  = (i) => { if (items.length > 1) setItems(items.filter((_, idx) => idx !== i)); };
  const handleAdd  = () => {
    const valid = items.map((s) => s.trim()).filter(Boolean);
    if (!valid.length) return;
    onAdd(`<ul>${valid.map((t) => `<li>${t}</li>`).join("")}</ul>`);
    setItems([""]);
  };

  return (
    <div className="space-y-3 rounded-xl border border-orange-100 bg-orange-50/50 p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-500">
        <ListIcon size={13} /> List Builder
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="shrink-0 text-xs text-slate-400">{i + 1}.</span>
            <input type="text" value={item} onChange={(e) => updateItem(i, e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRow(); } }}
              placeholder={`Item ${i + 1}`} className={inputCls} />
            <button type="button" onClick={() => removeRow(i)} disabled={items.length === 1}
              className="shrink-0 text-slate-400 hover:text-red-500 disabled:opacity-20 transition">
              <X size={14} />
            </button>
          </div>
        ))}
        <button type="button" onClick={addRow}
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-indigo-500 transition">
          <PlusCircle size={12} /> Add item
        </button>
      </div>
      <button type="button" onClick={handleAdd} disabled={!items.some((s) => s.trim())}
        className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-xs font-bold text-white hover:bg-orange-600 disabled:opacity-40 transition">
        <PlusCircle size={13} /> Add list block
      </button>
    </div>
  );
};

/* ─── Table Builder ───────────────────────────────────────────── */
const TableBuilder = ({ onAdd }) => {
  const [cols, setCols]   = useState(3);
  const [rows, setRows]   = useState(2);
  const [headers, setHeaders] = useState(["", "", ""]);
  const [cells, setCells] = useState([["", "", ""], ["", "", ""]]);

  const updateCols = (n) => {
    const num = Math.max(1, Math.min(10, Number(n)));
    setCols(num);
    setHeaders((prev) => {
      const h = [...prev];
      while (h.length < num) h.push("");
      return h.slice(0, num);
    });
    setCells((prev) =>
      prev.map((row) => {
        const r = [...row];
        while (r.length < num) r.push("");
        return r.slice(0, num);
      })
    );
  };

  const updateRows = (n) => {
    const num = Math.max(1, Math.min(20, Number(n)));
    setRows(num);
    setCells((prev) => {
      const next = [...prev];
      while (next.length < num) next.push(Array(cols).fill(""));
      return next.slice(0, num);
    });
  };

  const updateHeader = (ci, val) => {
    const h = [...headers]; h[ci] = val; setHeaders(h);
  };
  const updateCell = (ri, ci, val) => {
    const c = cells.map((r) => [...r]); c[ri][ci] = val; setCells(c);
  };

  const handleAdd = () => {
    const thead = `<thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>`;
    const tbody = `<tbody>${cells.map((row) => `<tr>${row.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("")}</tbody>`;
    onAdd(`<table style="width:100%;border-collapse:collapse;">${thead}${tbody}</table>`);
    setHeaders(Array(cols).fill(""));
    setCells(Array(rows).fill(null).map(() => Array(cols).fill("")));
  };

  return (
    <div className="space-y-3 rounded-xl border border-teal-100 bg-teal-50/50 p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-teal-600">
        <Table size={13} /> Table Builder
      </div>

      {/* Dimensions */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 shrink-0">Columns</label>
          <input type="number" min={1} max={10} value={cols}
            onChange={(e) => updateCols(e.target.value)}
            className="w-16 rounded-lg border border-[#1d3350] bg-[#0f1c2e] px-2 py-1.5 text-sm text-center text-slate-200 shadow-sm focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-100" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 shrink-0">Rows</label>
          <input type="number" min={1} max={20} value={rows}
            onChange={(e) => updateRows(e.target.value)}
            className="w-16 rounded-lg border border-[#1d3350] bg-[#0f1c2e] px-2 py-1.5 text-sm text-center text-slate-200 shadow-sm focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-100" />
        </div>
      </div>

      {/* Table input grid */}
      <div className="overflow-x-auto rounded-lg border border-[#1d3350] bg-[#0f1c2e]">
        <table className="w-full text-xs border-collapse">
          {/* Header row */}
          <thead>
            <tr className="bg-[#0d1b2e]">
              {headers.map((h, ci) => (
                <th key={ci} className="border border-[#1d3350] p-1">
                  <input type="text" value={h} onChange={(e) => updateHeader(ci, e.target.value)}
                    placeholder={`Header ${ci + 1}`}
                    className="w-full min-w-[80px] rounded border-0 bg-transparent px-1 py-0.5 text-xs font-semibold text-slate-300 placeholder-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-300 rounded" />
                </th>
              ))}
            </tr>
          </thead>
          {/* Data rows */}
          <tbody>
            {cells.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} className="border border-[#1d3350] p-1">
                    <input type="text" value={cell} onChange={(e) => updateCell(ri, ci, e.target.value)}
                      placeholder={`R${ri + 1}C${ci + 1}`}
                      className="w-full min-w-[80px] rounded border-0 bg-transparent px-1 py-0.5 text-xs text-slate-400 placeholder-slate-300 focus:outline-none focus:ring-1 focus:ring-teal-300 rounded" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button type="button" onClick={handleAdd}
        className="flex items-center gap-1.5 rounded-lg bg-teal-500 px-4 py-2 text-xs font-bold text-white hover:bg-teal-600 disabled:opacity-40 transition">
        <PlusCircle size={13} /> Add table block
      </button>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────── */
const AddBlogContentForm = ({ setContent, initialContent }) => {
  const [contentArray, setContentArray] = useState([]);
  const [activeType, setActiveType]     = useState(null);
  const [inputValue, setInputValue]     = useState("");
  const [className, setClassName]       = useState("");
  const [applied, setApplied]           = useState(false);

  useEffect(() => {
    if (initialContent) {
      const arr = parseContentToArray(initialContent);
      setContentArray(arr);
      if (arr.length) localStorage.setItem("content", JSON.stringify(arr));
    } else {
      try {
        const stored = localStorage.getItem("content");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) setContentArray(parsed);
        }
      } catch { /* ignore */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = (arr) => {
    setContentArray(arr);
    localStorage.setItem("content", JSON.stringify(arr));
    setApplied(false);
  };

  const addBlock = (html) => { commit([...contentArray, html]); setInputValue(""); setClassName(""); };

  const handleAddTextBlock = () => {
    if (!inputValue.trim() || !activeType) return;
    const html = buildHTML(activeType, inputValue, className);
    if (html) addBlock(html);
  };

  const handleRemove = (i) => { const arr = [...contentArray]; arr.splice(i, 1); commit(arr); };
  const handleEdit   = (i, html) => { const arr = [...contentArray]; arr[i] = html; commit(arr); };
  const handleMove   = (i, dir) => {
    const arr = [...contentArray]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; commit(arr);
  };

  const handleApply = () => {
    setContent(`<div class='reader-content'>${contentArray.join("")}</div>`);
    setApplied(true);
  };

  const groups     = [...new Set(BLOCK_TYPES.map((t) => t.group))];
  const isTextType = activeType && !["list", "image", "table"].includes(activeType);
  const ActiveIcon  = BLOCK_TYPES.find((t) => t.id === activeType)?.icon || Pilcrow;
  const activeLabel = BLOCK_TYPES.find((t) => t.id === activeType)?.label || "";

  return (
    <div className="rounded-2xl border border-[#1d3350] bg-[#0f1c2e] shadow-sm">

      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#17283f] px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-200">Content Builder</h2>
          <p className="text-xs text-slate-400">{contentArray.length} block{contentArray.length !== 1 ? "s" : ""}</p>
        </div>
        <button type="button" onClick={handleApply} disabled={contentArray.length === 0}
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold transition disabled:opacity-40 ${
            applied
              ? "border border-emerald-200 bg-emerald-50 text-emerald-600"
              : "bg-indigo-500 text-white hover:bg-indigo-600 shadow-sm"
          }`}>
          {applied
            ? <><CheckCircle2 size={13} /> Applied</>
            : <><SaveIcon size={13} /> Apply content</>}
        </button>
      </div>

      <div className="p-5 space-y-5">

        {/* Block type picker */}
        <div className="space-y-3">
          {groups.map((group) => (
            <div key={group}>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                {GROUP_LABELS[group]}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {BLOCK_TYPES.filter((t) => t.group === group).map((type) => (
                  <BlockTypeButton key={type.id} type={type} active={activeType === type.id}
                    onClick={() => setActiveType(activeType === type.id ? null : type.id)} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Input area */}
        {activeType && (
          <div className="rounded-xl border border-[#1d3350] bg-[#0d1b2e] p-4 space-y-3 shadow-inner">
            {activeType === "image" && (
              <ImageBlockInput onAdd={(html) => { addBlock(html); setActiveType(null); }} />
            )}
            {activeType === "list" && (
              <ListBuilder onAdd={(html) => { addBlock(html); setActiveType(null); }} />
            )}
            {activeType === "table" && (
              <TableBuilder onAdd={(html) => { addBlock(html); setActiveType(null); }} />
            )}
            {isTextType && (
              <>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-indigo-500">
                  <ActiveIcon size={13} /> {activeLabel}
                </div>
                <textarea value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && e.metaKey) handleAddTextBlock(); }}
                  rows={activeType === "paragraph" || activeType === "highlighted" ? 4 : 2}
                  placeholder={activeType === "code" ? "Paste your code here…" : `Enter ${activeLabel.toLowerCase()} text…`}
                  className={`${inputCls} resize-y ${activeType === "code" ? "font-mono text-xs" : ""}`}
                  autoFocus />
                <input type="text" value={className} onChange={(e) => setClassName(e.target.value)}
                  placeholder="CSS class (optional)" className={`${inputCls} font-mono text-xs`} />
                <div className="flex items-center gap-2">
                  <button type="button" onClick={handleAddTextBlock} disabled={!inputValue.trim()}
                    className="flex items-center gap-1.5 rounded-lg bg-indigo-500 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-600 disabled:opacity-40 transition shadow-sm">
                    <PlusCircle size={13} /> Add block
                  </button>
                  <span className="text-[10px] text-slate-400">⌘ + Enter</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Blocks list */}
        {contentArray.length > 0 ? (
          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              Blocks ({contentArray.length})
            </p>
            {contentArray.map((item, idx) => (
              <ContentItem key={`${idx}-${item.slice(0, 20)}`} html={item} index={idx}
                total={contentArray.length} onEdit={handleEdit} onRemove={handleRemove} onMove={handleMove} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[#1d3350] py-10 text-center bg-[#0d1b2e]">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0f1c2e] border border-[#1d3350] shadow-sm">
              <PlusCircle size={20} className="text-slate-300" />
            </div>
            <p className="text-sm text-slate-400">No blocks yet</p>
            <p className="text-xs text-slate-300">Pick a block type above to get started</p>
          </div>
        )}

        {/* Live preview */}
        {contentArray.length > 0 && (
          <details className="group rounded-xl border border-[#1d3350] bg-[#0f1c2e] shadow-sm">
            <summary className="flex cursor-pointer items-center justify-between px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-400 hover:text-slate-400 transition">
              <span>Live preview</span>
              <ChevronDown size={13} className="transition group-open:rotate-180" />
            </summary>
            <div className="border-t border-[#17283f] p-4">
              <div
                className="reader-content text-sm text-slate-300 [&_h1]:mb-2 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-slate-100 [&_h2]:mb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-slate-100 [&_h3]:mb-1 [&_h3]:font-bold [&_h3]:text-slate-200 [&_p]:mb-2 [&_p]:leading-relaxed [&_strong]:font-bold [&_strong]:text-slate-100 [&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-indigo-400 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-500 [&_.lead]:my-2 [&_.lead]:rounded-lg [&_.lead]:bg-amber-50 [&_.lead]:px-4 [&_.lead]:py-3 [&_.lead]:text-amber-700 [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-[#13233a] [&_pre]:p-4 [&_pre]:font-mono [&_pre]:text-xs [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_figure]:my-4 [&_img]:max-w-[min(400px,40vw)] [&_img]:rounded-lg [&_figcaption]:mt-1 [&_figcaption]:text-xs [&_figcaption]:text-slate-400 [&_table]:w-full [&_table]:border-collapse [&_table]:my-3 [&_th]:border [&_th]:border-[#1d3350] [&_th]:bg-[#0d1b2e] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-slate-300 [&_td]:border [&_td]:border-[#1d3350] [&_td]:px-3 [&_td]:py-2 [&_td]:text-slate-400"
                dangerouslySetInnerHTML={{ __html: contentArray.join("") }}
              />
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

export default AddBlogContentForm;